import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applyReadingEffectValue,
  resetReadingEffectValue,
} from "../shared/page-reading-effects.ts";

const attribute = "data-migrosone-a11y-reading-mode";
const readingVariables = [
  "--migrosone-a11y-reading-max-width",
  "--migrosone-a11y-reading-padding-inline",
] as const;
const allowedWidths = ["narrow", "comfortable", "wide"] as const;

type ReadingWidth = (typeof allowedWidths)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readBooleanDefault(config: unknown) {
  if (!isRecord(config)) {
    return false;
  }

  if (typeof config.enabledByDefault === "boolean") {
    return config.enabledByDefault;
  }

  return typeof config.defaultEnabled === "boolean" ? config.defaultEnabled : false;
}

function readWidth(config: unknown): ReadingWidth {
  const configuredWidth =
    isRecord(config) && typeof config.width === "string"
      ? config.width.trim()
      : "comfortable";

  return allowedWidths.includes(configuredWidth as ReadingWidth)
    ? (configuredWidth as ReadingWidth)
    : "comfortable";
}

function resolveEnabled(config: unknown, preference: unknown) {
  return typeof preference === "boolean" ? preference : readBooleanDefault(config);
}

function maxWidthFor(width: ReadingWidth) {
  if (width === "narrow") {
    return "56ch";
  }

  if (width === "wide") {
    return "78ch";
  }

  return "68ch";
}

function resetReadingModeVariables(document: Document) {
  for (const variable of readingVariables) {
    resetReadingEffectValue({ attribute, document, variable });
  }
}

function applyReadingModeStyle(document: Document, config: unknown) {
  applyReadingEffectValue({
    attribute,
    document,
    value: maxWidthFor(readWidth(config)),
    variable: "--migrosone-a11y-reading-max-width",
  });
  applyReadingEffectValue({
    attribute,
    document,
    value: "16px",
    variable: "--migrosone-a11y-reading-padding-inline",
  });
}

const definition = getFeaturePanelDefinition("reading-mode");

if (!definition) {
  throw new Error("Missing reading-mode feature definition");
}

export const readingModeFeatureModule: RuntimeFeatureModule = {
  key: "reading-mode",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const wrapper = document.createElement("label");
    const input = document.createElement("input");
    const text = document.createElement("span");

    wrapper.className = "migrosone-a11y-widget-feature-control";
    input.type = "checkbox";
    input.checked = resolveEnabled(config, preference);
    text.textContent = "Okuma modu";

    input.addEventListener("change", () => {
      onPreferenceChange(input.checked);
    });

    wrapper.append(input, text);
    return wrapper;
  },
  apply({ config, document, preference }) {
    resetReadingModeVariables(document);
    if (!resolveEnabled(config, preference)) {
      return;
    }

    applyReadingModeStyle(document, config);
  },
  reset({ document }) {
    resetReadingModeVariables(document);
  },
};
